import {
    ArrowPathIcon,
    CheckCircleIcon,
    DocumentTextIcon,
    TrashIcon,
    XCircleIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline';

import { IconButton } from '@/components/core/IconButton';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/services/utils';

import { type UploadItem } from './types';
import { useEstimatedRemainingTime } from './useEstimatedRemainingTime';
import { formatBytes, formatRemainingTime } from './utils';

interface FileUploadDetailsItemProps {
    item: UploadItem;
    onRemove?: (item: UploadItem) => void;
    onRetry?: (item: UploadItem) => void;
}

const FileUploadDetailsItem = ({ item, onRemove, onRetry }: FileUploadDetailsItemProps) => {
    const { t } = useTranslation();

    const status = item.status ?? 'idle';
    const isError = status === 'error';
    const isUploading = status === 'uploading';
    const isSuccess = status === 'success';
    const progress = Math.max(0, Math.min(100, item.progress ?? (isSuccess ? 100 : 0)));

    const estimatedRemainingMs = useEstimatedRemainingTime(
        progress,
        item.startedAt,
        isUploading,
    );
    const resolvedRemainingMs = item.remainingMs ?? estimatedRemainingMs;
    const remainingText =
        isUploading && typeof resolvedRemainingMs === 'number'
            ? formatRemainingTime(resolvedRemainingMs)
            : undefined;

    const showProgressBar = isUploading;
    const showProgressPercent = isUploading;

    const statusText = isSuccess
        ? t('UPLOADER.UPLOAD_COMPLETED')
        : isError
            ? t('UPLOADER.UPLOAD_FAILED')
            : undefined;

    return (
        <div
            className={cn(
                'tw-flex tw-flex-col tw-w-full',
                isError && item.errorMessage ? 'tw-gap-2' : 'tw-gap-1',
            )}
            data-testid={`uploader-file-item-${item.id}`}
        >
            <div
                className={cn(
                    'tw-flex tw-flex-col tw-w-full tw-p-3 tw-rounded-md',
                    'tw-bg-fill-secondary',
                )}
            >
                <div className={cn('tw-flex tw-flex-col tw-w-full', isUploading && 'tw-gap-3')}>
                    <div className="tw-flex tw-items-center tw-gap-2 tw-w-full">
                        <DocumentTextIcon
                            className="tw-w-9 tw-h-9 tw-shrink-0 tw-icon-inactive"
                            aria-hidden="true"
                        />

                        <div className="tw-flex tw-flex-col tw-gap-1 tw-flex-1 tw-min-w-0">
                            <div className="tw-flex tw-items-center tw-gap-2 tw-w-full">
                                <div className="tw-flex tw-items-center tw-gap-1 tw-flex-1 tw-min-w-0">
                                    <div
                                        className="tw-text-t-primary tw-text-t-sm lg:tw-text-t-base"
                                        title={item.file.name}
                                    >
                                        {item.file.name}
                                    </div>
                                    {isSuccess && (
                                        <CheckCircleIcon
                                            className="tw-w-5 tw-h-5 lg:tw-w-6 lg:tw-h-6 tw-shrink-0 tw-icon-success"
                                            aria-hidden="true"
                                        />
                                    )}
                                    {isError && (
                                        <XCircleIcon
                                            className="tw-w-5 tw-h-5 lg:tw-w-6 lg:tw-h-6 tw-shrink-0 tw-icon-destructive"
                                            aria-hidden="true"
                                        />
                                    )}
                                </div>

                                {isUploading && onRemove && (
                                    <IconButton
                                        type="button"
                                        icon={XMarkIcon}
                                        size="sm"
                                        variant="dark"
                                        className="tw-icon-primary !tw-h-4 !tw-w-4 !tw-min-h-0 !tw-min-w-0 !tw-p-0 !tw-text-[0] !tw-leading-none [&_svg]:!tw-h-4 [&_svg]:!tw-w-4"
                                        aria-label={t('UPLOADER.REMOVE_FILE')}
                                        onClick={() => onRemove(item)}
                                        data-testid={`uploader-file-item-remove-${item.id}`}
                                    />
                                )}
                            </div>

                            <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-w-full">
                                <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
                                    <div className="tw-text-t-xs tw-text-t-secondary">
                                        {formatBytes(item.file.size)}
                                    </div>
                                    {remainingText && (
                                        <>
                                            <span
                                                className="tw-w-0.5 tw-h-0.5 tw-rounded-full tw-bg-current tw-text-t-secondary tw-shrink-0"
                                                aria-hidden="true"
                                            />
                                            <div className="tw-text-t-xs tw-text-t-secondary">
                                                {t(remainingText.key, remainingText.values)}
                                            </div>
                                        </>
                                    )}
                                    {statusText && (
                                        <>
                                            <span
                                                className="tw-w-0.5 tw-h-0.5 tw-rounded-full tw-bg-current tw-text-t-secondary tw-shrink-0"
                                                aria-hidden="true"
                                            />
                                            <div
                                                className={`tw-text-t-xs ${isError ? 'tw-text-error' : 'tw-text-t-secondary'}`}
                                            >
                                                {statusText}
                                            </div>
                                        </>
                                    )}
                                </div>
                                {showProgressPercent && (
                                    <div className="tw-text-t-xs tw-text-t-secondary tw-shrink-0">
                                        {Math.round(progress)}%
                                    </div>
                                )}
                            </div>
                        </div>

                        {!isUploading && (onRemove || (isError && onRetry)) && (
                            <div className="tw-flex tw-items-center tw-gap-2 tw-shrink-0">
                                {isError && onRetry && (
                                    <IconButton
                                        type="button"
                                        icon={ArrowPathIcon}
                                        size="lg"
                                        variant="dark"
                                        className="tw-icon-primary"
                                        aria-label={t('UPLOADER.RETRY_UPLOAD')}
                                        onClick={() => onRetry(item)}
                                        data-testid={`uploader-file-item-retry-${item.id}`}
                                    />
                                )}
                                {onRemove && (
                                    <IconButton
                                        type="button"
                                        icon={TrashIcon}
                                        size="lg"
                                        variant="dark"
                                        className="tw-icon-primary"
                                        aria-label={t('UPLOADER.REMOVE_FILE')}
                                        onClick={() => onRemove(item)}
                                        data-testid={`uploader-file-item-remove-${item.id}`}
                                    />
                                )}
                            </div>
                        )}
                    </div>

                    {showProgressBar && (
                        <div
                            className="uploader-progress-track"
                            role="progressbar"
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-valuenow={Math.round(progress)}
                            aria-label={item.file.name}
                        >
                            <div
                                className={cn(
                                    'uploader-progress-fill',
                                    isError && 'uploader-progress-fill--error',
                                )}
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    )}
                </div>
            </div>

            {isError && item.errorMessage && (
                <div
                    role="alert"
                    className="tw-text-t-xs lg:tw-text-t-sm tw-text-error tw-w-full"
                    data-testid={`uploader-file-item-error-${item.id}`}
                >
                    {t(item.errorMessage)}
                </div>
            )}
        </div>
    );
};

FileUploadDetailsItem.displayName = 'FileUploadDetailsItem';

export { FileUploadDetailsItem };
